/**
 * Printable calendar HTML builder.
 * Pure functions — given events/tasks/planning todos and a month range,
 * produce a self-contained HTML document for the print window.
 */

import type { Event, PlanningTodoWithContext } from '@peles/shared';
import type { CalendarTaskItem } from '../api/event-ops';
import { hebrewDateParts, hebrewMonthYearString, getJewishHolidays } from './hebrewCalendar';
import { getUSFederalHolidays } from './usHolidays';

export type CalendarPrintFormat = 'ONE_PER_PAGE' | 'TWO_PER_PAGE' | 'FOUR_PER_PAGE' | 'LIST';

export interface CalendarPrintFormatConfig {
  id: CalendarPrintFormat;
  label: string;
  description: string;
  orientation: 'landscape' | 'portrait';
}

export const CALENDAR_PRINT_FORMATS: CalendarPrintFormatConfig[] = [
  {
    id: 'ONE_PER_PAGE',
    label: '1 Month / Page',
    description: 'One full month per page, landscape',
    orientation: 'landscape',
  },
  {
    id: 'TWO_PER_PAGE',
    label: '2 Months / Page',
    description: 'Two months stacked per page, portrait',
    orientation: 'portrait',
  },
  {
    id: 'FOUR_PER_PAGE',
    label: '4 Months / Page',
    description: 'Four months per page (2×2), landscape',
    orientation: 'landscape',
  },
  {
    id: 'LIST',
    label: 'Event List',
    description: 'Chronological list of everything in the range',
    orientation: 'portrait',
  },
];

export interface CalendarPrintOptions {
  fromYear: number;
  /** 0-based month */
  fromMonth: number;
  toYear: number;
  /** 0-based month */
  toMonth: number;
  format: CalendarPrintFormat;
  events: Event[];
  tasks: CalendarTaskItem[];
  planning: PlanningTodoWithContext[];
}

export interface YearMonth {
  year: number;
  /** 0-based month */
  month: number;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EVENT_STATUS_COLORS: Record<string, string> = {
  CONFIRMED: '#22c55e',
  PLANNING: '#3b82f6',
  DRAFT: '#9ca3af',
  IN_PROGRESS: '#a855f7',
  COMPLETED: '#14b8a6',
  CANCELLED: '#f87171',
};

const TASK_COLOR = '#6366f1';
const PLANNING_COLOR = '#0d9488';

/** Parse a date string (ISO or YYYY-MM-DD) as a LOCAL date to avoid UTC-midnight timezone shifts. */
export function parseDateLocal(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  const part = dateStr.split('T')[0];
  const [y, m, d] = part.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Inclusive list of {year, month} between from and to. Empty if from > to. */
export function monthsInRange(
  fromYear: number,
  fromMonth: number,
  toYear: number,
  toMonth: number,
): YearMonth[] {
  const months: YearMonth[] = [];
  let y = fromYear;
  let m = fromMonth;
  while (y < toYear || (y === toYear && m <= toMonth)) {
    months.push({ year: y, month: m });
    m++;
    if (m > 11) {
      m = 0;
      y++;
    }
  }
  return months;
}

function esc(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function dayKey(year: number, month: number, day: number): string {
  return `${year}-${month}-${day}`;
}

interface DayContentMaps {
  eventsByDay: Map<string, Event[]>;
  tasksByDay: Map<string, CalendarTaskItem[]>;
  planningByDay: Map<string, PlanningTodoWithContext[]>;
}

function buildDayMaps(opts: CalendarPrintOptions): DayContentMaps {
  const rangeStart = new Date(opts.fromYear, opts.fromMonth, 1);
  const rangeEnd = new Date(opts.toYear, opts.toMonth + 1, 0, 23, 59, 59, 999);

  const eventsByDay = new Map<string, Event[]>();
  opts.events.forEach((ev) => {
    const start = new Date(ev.startAt);
    const end = ev.endAt ? new Date(ev.endAt) : start;
    if (end < rangeStart || start > rangeEnd) return;
    const startKey = dayKey(start.getFullYear(), start.getMonth(), start.getDate());
    const list = eventsByDay.get(startKey) ?? [];
    list.push(ev);
    eventsByDay.set(startKey, list);
    if (ev.endAt) {
      const stop = end <= rangeEnd ? end : rangeEnd;
      const cur = new Date(start);
      cur.setDate(cur.getDate() + 1);
      if (cur < rangeStart) cur.setTime(rangeStart.getTime());
      while (cur <= stop) {
        const k = dayKey(cur.getFullYear(), cur.getMonth(), cur.getDate());
        const evList = eventsByDay.get(k) ?? [];
        if (!evList.includes(ev)) evList.push(ev);
        eventsByDay.set(k, evList);
        cur.setDate(cur.getDate() + 1);
      }
    }
  });

  const tasksByDay = new Map<string, CalendarTaskItem[]>();
  opts.tasks.forEach((task) => {
    const d = parseDateLocal(task.dueDate);
    if (!d) return;
    const k = dayKey(d.getFullYear(), d.getMonth(), d.getDate());
    const list = tasksByDay.get(k) ?? [];
    list.push(task);
    tasksByDay.set(k, list);
  });

  const planningByDay = new Map<string, PlanningTodoWithContext[]>();
  opts.planning.forEach((todo) => {
    const d = parseDateLocal(todo.dueDate ? String(todo.dueDate) : null);
    if (!d) return;
    const k = dayKey(d.getFullYear(), d.getMonth(), d.getDate());
    const list = planningByDay.get(k) ?? [];
    list.push(todo);
    planningByDay.set(k, list);
  });

  return { eventsByDay, tasksByDay, planningByDay };
}

/** Hebrew label spanning a Gregorian month, e.g. "אלול תשפ״ו – תשרי תשפ״ז". */
function hebrewMonthSpanLabel(year: number, month: number): string {
  const first = hebrewMonthYearString(new Date(year, month, 1));
  const lastDay = new Date(year, month + 1, 0).getDate();
  const last = hebrewMonthYearString(new Date(year, month, lastDay));
  return first === last ? first : `${first} – ${last}`;
}

function renderMonthGrid(ym: YearMonth, maps: DayContentMaps): string {
  const { year, month } = ym;
  const usHolidays = getUSFederalHolidays(year);
  const jewishHolidays = getJewishHolidays(year, month);
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7;

  let cellsHtml = '';
  for (let i = 0; i < totalCells; i++) {
    if (i % 7 === 0) cellsHtml += '<tr>';

    const day = i - firstDow + 1;
    if (day < 1 || day > daysInMonth) {
      cellsHtml += '<td class="cell out"></td>';
    } else {
      const date = new Date(year, month, day);
      const key = dayKey(year, month, day);
      const usHoliday = usHolidays.get(key);
      const jewishList = jewishHolidays.get(key) ?? [];
      const dayEvents = maps.eventsByDay.get(key) ?? [];
      const dayTasks = maps.tasksByDay.get(key) ?? [];
      const dayPlanning = maps.planningByDay.get(key) ?? [];
      const dow = date.getDay();
      const isWeekend = dow === 0 || dow === 6;

      let hebrewLabel = '';
      try {
        const parts = hebrewDateParts(date);
        hebrewLabel = `${parts.day} ${parts.month}`;
      } catch {
        hebrewLabel = '';
      }

      const cellClass = [
        'cell',
        isWeekend ? 'we' : '',
        usHoliday ? 'hol-us-bg' : jewishList.length > 0 ? 'hol-jw-bg' : '',
      ]
        .filter(Boolean)
        .join(' ');

      let content = `<div class="cellhead"><span class="gday">${day}</span><span class="hday" dir="rtl">${esc(hebrewLabel)}</span></div>`;
      if (usHoliday) content += `<div class="hol-us">🇺🇸 ${esc(usHoliday)}</div>`;
      jewishList.forEach((jh) => {
        content += `<div class="hol-jw" dir="rtl">✡ ${esc(jh)}</div>`;
      });
      dayEvents.forEach((ev) => {
        const color = EVENT_STATUS_COLORS[ev.status] ?? '#9ca3af';
        content += `<div class="item" style="background:${color};">${esc(ev.name)}</div>`;
      });
      dayTasks.forEach((task) => {
        content += `<div class="item${task.isDone ? ' done' : ''}" style="background:${TASK_COLOR};">${task.isDone ? '☑' : '☐'} ${esc(task.title)}</div>`;
      });
      dayPlanning.forEach((todo) => {
        content += `<div class="item${todo.isDone ? ' done' : ''}" style="background:${PLANNING_COLOR};">${todo.isDone ? '☑' : '☐'} ${esc(todo.title)}</div>`;
      });

      cellsHtml += `<td class="${cellClass}">${content}</td>`;
    }

    if (i % 7 === 6) cellsHtml += '</tr>';
  }

  const headRow = DAY_NAMES.map((d) => `<th>${d}</th>`).join('');

  return `<div class="month">
  <div class="month-title"><span class="gname">${MONTH_NAMES[month]} ${year}</span><span class="hname" dir="rtl">${esc(hebrewMonthSpanLabel(year, month))}</span></div>
  <table class="grid"><thead><tr>${headRow}</tr></thead><tbody>${cellsHtml}</tbody></table>
</div>`;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ─── List format ─────────────────────────────────────────────────────────────

interface ListRow {
  date: Date;
  sortOrder: number;
  html: string;
}

function formatListDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function safeHebrewLabel(d: Date): string {
  try {
    const parts = hebrewDateParts(d);
    return `${parts.day} ${parts.month}`;
  } catch {
    return '';
  }
}

function listRowHtml(
  d: Date,
  badge: string,
  badgeColor: string,
  title: string,
  detail: string,
): string {
  return `<tr>
  <td class="ldate">${esc(formatListDate(d))}</td>
  <td class="lheb" dir="rtl">${esc(safeHebrewLabel(d))}</td>
  <td><span class="badge" style="background:${badgeColor};">${esc(badge)}</span></td>
  <td class="ltitle">${title}</td>
  <td class="ldetail">${esc(detail)}</td>
</tr>`;
}

function renderList(opts: CalendarPrintOptions, months: YearMonth[]): string {
  const rows: ListRow[] = [];
  const rangeStart = new Date(opts.fromYear, opts.fromMonth, 1);
  const rangeEnd = new Date(opts.toYear, opts.toMonth + 1, 0, 23, 59, 59);

  opts.events.forEach((ev) => {
    const start = new Date(ev.startAt);
    const end = ev.endAt ? new Date(ev.endAt) : start;
    if (end < rangeStart || start > rangeEnd) return;
    let dateDetail = '';
    if (ev.endAt) {
      const endD = new Date(ev.endAt);
      if (
        endD.getFullYear() !== start.getFullYear() ||
        endD.getMonth() !== start.getMonth() ||
        endD.getDate() !== start.getDate()
      ) {
        dateDetail = `until ${endD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · `;
      }
    }
    const color = EVENT_STATUS_COLORS[ev.status] ?? '#9ca3af';
    rows.push({
      date: start,
      sortOrder: 0,
      html: listRowHtml(
        start,
        'Event',
        color,
        `<strong>${esc(ev.name)}</strong>`,
        `${dateDetail}${ev.status.toLowerCase().replace('_', ' ')}`,
      ),
    });
  });

  opts.tasks.forEach((task) => {
    const d = parseDateLocal(task.dueDate);
    if (!d || d < rangeStart || d > rangeEnd) return;
    rows.push({
      date: d,
      sortOrder: 1,
      html: listRowHtml(
        d,
        'Task',
        TASK_COLOR,
        `${task.isDone ? '☑' : '☐'} ${task.isDone ? `<s>${esc(task.title)}</s>` : esc(task.title)}`,
        task.section.checklist.title ?? 'Task List',
      ),
    });
  });

  opts.planning.forEach((todo) => {
    const d = parseDateLocal(todo.dueDate ? String(todo.dueDate) : null);
    if (!d || d < rangeStart || d > rangeEnd) return;
    const detail = [todo.planningListTitle ?? 'Planning', todo.eventName]
      .filter(Boolean)
      .join(' · ');
    rows.push({
      date: d,
      sortOrder: 2,
      html: listRowHtml(
        d,
        'Planning',
        PLANNING_COLOR,
        `${todo.isDone ? '☑' : '☐'} ${todo.isDone ? `<s>${esc(todo.title)}</s>` : esc(todo.title)}`,
        detail,
      ),
    });
  });

  months.forEach(({ year, month }) => {
    const usHolidays = getUSFederalHolidays(year);
    const jewishHolidays = getJewishHolidays(year, month);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; day++) {
      const key = dayKey(year, month, day);
      const d = new Date(year, month, day);
      const us = usHolidays.get(key);
      if (us) {
        rows.push({
          date: d,
          sortOrder: 3,
          html: listRowHtml(d, 'Holiday', '#2563eb', `🇺🇸 ${esc(us)}`, 'US Holiday'),
        });
      }
      (jewishHolidays.get(key) ?? []).forEach((jh) => {
        rows.push({
          date: d,
          sortOrder: 4,
          html: listRowHtml(
            d,
            'Holiday',
            '#d97706',
            `✡ <span dir="rtl">${esc(jh)}</span>`,
            'Jewish Holiday',
          ),
        });
      });
    }
  });

  rows.sort((a, b) => a.date.getTime() - b.date.getTime() || a.sortOrder - b.sortOrder);

  if (rows.length === 0) {
    return '<p class="empty">Nothing scheduled in the selected range.</p>';
  }

  // Group rows under month headers
  let html = '';
  let currentMonthKey = '';
  rows.forEach((row) => {
    const mk = `${row.date.getFullYear()}-${row.date.getMonth()}`;
    if (mk !== currentMonthKey) {
      if (currentMonthKey !== '') html += '</tbody></table>';
      currentMonthKey = mk;
      html += `<h2 class="list-month"><span>${MONTH_NAMES[row.date.getMonth()]} ${row.date.getFullYear()}</span><span class="hname" dir="rtl">${esc(hebrewMonthSpanLabel(row.date.getFullYear(), row.date.getMonth()))}</span></h2>`;
      html += `<table class="list"><thead><tr><th>Date</th><th>Hebrew Date</th><th>Type</th><th>Title</th><th>Details</th></tr></thead><tbody>`;
    }
    html += row.html;
  });
  html += '</tbody></table>';
  return html;
}

// ─── Document assembly ───────────────────────────────────────────────────────

function rangeTitle(opts: CalendarPrintOptions): string {
  const from = `${MONTH_NAMES[opts.fromMonth]} ${opts.fromYear}`;
  const to = `${MONTH_NAMES[opts.toMonth]} ${opts.toYear}`;
  return from === to ? from : `${from} – ${to}`;
}

const BASE_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #111827; }
  .page { page-break-after: always; }
  .page:last-child { page-break-after: auto; }
  .month-title { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; }
  .month-title .gname { font-weight: 700; }
  .month-title .hname { color: #6b7280; }
  table.grid { width: 100%; border-collapse: collapse; table-layout: fixed; }
  table.grid th { border: 1px solid #d1d5db; background: #f3f4f6; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
  td.cell { border: 1px solid #d1d5db; vertical-align: top; overflow: hidden; }
  td.cell.out { background: #f9fafb; }
  td.cell.we { background: #f3f4f6; }
  td.cell.hol-us-bg { background: #eff6ff; }
  td.cell.hol-jw-bg { background: #fffbeb; }
  .cellhead { display: flex; justify-content: space-between; align-items: baseline; }
  .gday { font-weight: 700; }
  .hday { color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .hol-us { color: #2563eb; font-weight: 600; }
  .hol-jw { color: #b45309; font-weight: 600; text-align: right; }
  .item { color: #fff; border-radius: 2px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .item.done { opacity: 0.55; }
  .hol-us, .hol-jw { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .doc-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px; }
  .doc-header h1 { font-size: 15px; }
  .doc-header .sub { color: #6b7280; font-size: 11px; }
`;

const FORMAT_STYLES: Record<CalendarPrintFormat, string> = {
  ONE_PER_PAGE: `
  @page { size: letter landscape; margin: 8mm; }
  .month-title { font-size: 16px; }
  table.grid { height: 178mm; }
  table.grid th { font-size: 9px; padding: 3px 0; }
  td.cell { padding: 2px 3px; height: 28mm; }
  .gday { font-size: 12px; }
  .hday { font-size: 8px; max-width: 70%; }
  .hol-us, .hol-jw { font-size: 8px; margin-top: 1px; }
  .item { font-size: 8px; padding: 1px 3px; margin-top: 1px; }
`,
  TWO_PER_PAGE: `
  @page { size: letter portrait; margin: 8mm; }
  .month + .month { margin-top: 6mm; }
  .month-title { font-size: 13px; }
  table.grid { height: 118mm; }
  table.grid th { font-size: 8px; padding: 2px 0; }
  td.cell { padding: 1px 2px; height: 18mm; }
  .gday { font-size: 10px; }
  .hday { font-size: 7px; max-width: 68%; }
  .hol-us, .hol-jw { font-size: 6.5px; }
  .item { font-size: 6.5px; padding: 0.5px 2px; margin-top: 1px; }
`,
  FOUR_PER_PAGE: `
  @page { size: letter landscape; margin: 7mm; }
  .page { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 4mm; }
  .month { min-height: 0; }
  .month-title { font-size: 11px; }
  table.grid { height: 88mm; }
  table.grid th { font-size: 6.5px; padding: 1px 0; }
  td.cell { padding: 1px; height: 13mm; }
  .gday { font-size: 8px; }
  .hday { font-size: 5.5px; max-width: 66%; }
  .hol-us, .hol-jw { font-size: 5.5px; }
  .item { font-size: 5.5px; padding: 0.5px 1.5px; margin-top: 0.5px; border-radius: 1px; }
`,
  LIST: `
  @page { size: letter portrait; margin: 12mm; }
  body { font-size: 11px; }
  .list-month { font-size: 13px; margin: 14px 0 6px; display: flex; justify-content: space-between; align-items: baseline; page-break-after: avoid; }
  .list-month .hname { color: #6b7280; font-size: 11px; font-weight: 500; }
  table.list { width: 100%; border-collapse: collapse; }
  table.list th { text-align: left; font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #d1d5db; padding: 3px 6px; }
  table.list td { border-bottom: 1px solid #f3f4f6; padding: 3px 6px; vertical-align: top; }
  td.ldate { white-space: nowrap; width: 110px; }
  td.lheb { white-space: nowrap; width: 80px; color: #6b7280; }
  .badge { color: #fff; border-radius: 8px; padding: 1px 7px; font-size: 8.5px; font-weight: 600; white-space: nowrap; }
  td.ltitle { width: 40%; }
  td.ldetail { color: #6b7280; }
  .empty { color: #9ca3af; font-style: italic; margin-top: 20px; }
`,
};

export function buildCalendarPrintHtml(opts: CalendarPrintOptions): string {
  const months = monthsInRange(opts.fromYear, opts.fromMonth, opts.toYear, opts.toMonth);
  const title = `Calendar — ${rangeTitle(opts)}`;

  let bodyHtml = '';
  if (opts.format === 'LIST') {
    bodyHtml = `<div class="doc-header"><h1>${esc(title)}</h1><span class="sub">Events · Tasks · Planning · Holidays</span></div>${renderList(opts, months)}`;
  } else {
    const maps = buildDayMaps(opts);
    const perPage = opts.format === 'ONE_PER_PAGE' ? 1 : opts.format === 'TWO_PER_PAGE' ? 2 : 4;
    bodyHtml = chunk(months, perPage)
      .map(
        (group) =>
          `<div class="page">${group.map((ym) => renderMonthGrid(ym, maps)).join('')}</div>`,
      )
      .join('');
  }

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>${esc(title)}</title>
<style>${BASE_STYLES}${FORMAT_STYLES[opts.format]}</style>
</head>
<body>
${bodyHtml}
</body>
</html>`;
}
